/** @ken/module-sales_orders — frontend entry point.
 *
 * Mirrors @ken/module-invoicing shape. Sales orders are pre-invoice
 * customer commitments with line items + Job-or-Customer parentage +
 * SO-specific lifecycle (expected_ship_date, is_fully_invoiced,
 * is_manually_closed). Source-canonical fields + custom_field_values
 * render via the unified tab system (canonical layout + field
 * definitions, merged client-side per ADR-071).
 *
 * 0.2.0 — story-page adoption (ADR-0081). Adds /sales-orders/$id/overview,
 * /line-items, /related, /notes full-page routes. The in-list Sheet is
 * RETIRED; the SO-number cell is a real stretched <Link> to the story page.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { SalesOrderStorySearch } from "./routes/story/sales-order-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadSalesOrderStoryTabs = () => import("./routes/story/sales-order-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const SalesOrderCreatePage = lazyScreen(
  () => import("./routes/sales-order-create"),
  "SalesOrderCreatePage",
);
const SalesOrderEditPage = lazyScreen(
  () => import("./routes/sales-order-edit"),
  "SalesOrderEditPage",
);
const SalesOrderListPage = lazyScreen(
  () => import("./routes/sales-order-list"),
  "SalesOrderListPage",
);
const SalesOrderStoryDetail = lazyScreen(
  () => import("./routes/story/sales-order-story-detail"),
  "SalesOrderStoryDetail",
);
const SalesOrderActivityRoute = lazyScreen(
  loadSalesOrderStoryTabs,
  "SalesOrderActivityRoute",
);
const SalesOrderLineItemsRoute = lazyScreen(
  loadSalesOrderStoryTabs,
  "SalesOrderLineItemsRoute",
);
const SalesOrderNotesRoute = lazyScreen(
  loadSalesOrderStoryTabs,
  "SalesOrderNotesRoute",
);
const SalesOrderOverviewRoute = lazyScreen(
  loadSalesOrderStoryTabs,
  "SalesOrderOverviewRoute",
);
const SalesOrderRelatedRoute = lazyScreen(
  loadSalesOrderStoryTabs,
  "SalesOrderRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-orders",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof.
    validateSearch: (raw: Record<string, unknown>): SalesOrderListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: SalesOrderListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-orders/new",
    component: SalesOrderCreatePage,
  });

  // Edit (backlog B2): a focused FULL-PAGE form, deep-linkable, NOT nested
  // under the story shell. Parented directly to the module root so it renders
  // standalone (no StoryPageDetail header/tabs around it). Registered BEFORE
  // the `sales-orders/$id` parent so the `/edit` literal segment is matched.
  const editRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-orders/$id/edit",
    component: SalesOrderEditPage,
  });

  // ── Story-page detail (ADR-0081): parent sales-orders/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "sales-orders/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): SalesOrderStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: SalesOrderStoryDetail,
  });

  // Index redirect sales-orders/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/sales-orders/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: SalesOrderOverviewRoute,
  });
  const lineItemsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "line-items",
    component: SalesOrderLineItemsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: SalesOrderRelatedRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: SalesOrderActivityRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: SalesOrderNotesRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      lineItemsRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [listRoute, createRouteEntry, editRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface SalesOrderListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
